import { Show, type Accessor, type JSXElement } from 'solid-js'
import type {
  CatalogueCurrency,
  PaidCataloguePrice,
} from '~/lib/catalogue-price'
import { intlLocale } from '~/lib/params'
import { m } from '~/paraglide/messages'
import type { CatalogueProduct } from '~/services/catalogue'
import { canAfford, type ShopBalances } from '~/store/session-ledger'
import {
  ConfirmPriceCta,
  createEffectivePriceParts,
} from './effective-price-parts'
import {
  captureSubmittedAmount,
  freeUnlockAdapter,
  softPurchaseAdapter,
} from './purchase-flow/adapters'
import { PurchaseFlow } from './purchase-flow/surface'
import { RewardRevealScaffold } from './reward-reveal-scaffold'

const numberFormatter = new Intl.NumberFormat(intlLocale)

function SuccessAcknowledgement(props: {
  balances: Accessor<ShopBalances | undefined>
  onDone: () => void
  product: CatalogueProduct
}) {
  return (
    <RewardRevealScaffold
      balanceTestIdPrefix="unlock-success-"
      caption={props.product.title}
      coinValue={props.balances()?.coin ?? 0}
      ctaLabel={m.btn_got_it()}
      ctaTestId="unlock-success-done"
      gemValue={props.balances()?.gem ?? 0}
      onCta={() => props.onDone()}
      testId="tree-unlock-success"
      title={
        props.product.content === 'land_skin'
          ? m.new_land_unlocked()
          : m.new_plant_unlocked()
      }
      variant="tree"
    >
      <img
        data-testid="unlock-success-art"
        src={props.product.productImage.src}
        alt=""
        class="size-full object-contain"
      />
    </RewardRevealScaffold>
  )
}

interface UnlockFlowProps {
  balances: Accessor<ShopBalances | undefined>
  onCancel: () => void
  onDone: () => void
  // paidAmount is the effective price submitted, 0 for a free product.
  onSuccess: (product: CatalogueProduct, paidAmount: number) => void
  // Follows a free unlock's in-flight command, which the detail's Unlock
  // button presents (GLOSSARY.md "Free product").
  onPendingChange?: (pending: boolean) => void
  onTopUp: (currency: CatalogueCurrency) => void
  product: CatalogueProduct
  serverNow: Accessor<number>
  userId: number
}

// The unlock flow for trees AND land skins — a land-skin purchase runs this
// same flow and unlock rules (GLOSSARY.md "Land-skin detail"). A free product
// skips the confirmation and submits at once (GLOSSARY.md "Free product").
// A priced one's confirmation title follows the content kind — "Unlock this
// tree species?" (unlock_tree_confirm_title) or "Unlock this Land Style?"
// (unlock_land_confirm_title) — as the unlock success reveal's title does
// (GLOSSARY.md "Unlock success reveal"). The confirmation description still
// reuses the tree-worded keys (unlock_tree_confirm_with_coins,
// purchase_confirm_with_time_crystals) for land skins until dedicated copy
// exists in Lokalise (#443).
export function TreeUnlockFlow(props: UnlockFlowProps) {
  const paidPrice = () => {
    const { price } = props.product
    return price.kind === 'paid' ? price : undefined
  }
  const reveal = () => (
    <SuccessAcknowledgement
      balances={props.balances}
      onDone={() => props.onDone()}
      product={props.product}
    />
  )

  return (
    <Show
      when={paidPrice()}
      fallback={
        <PurchaseFlow
          adapter={freeUnlockAdapter({
            productId: () => props.product.id,
            userId: () => props.userId,
          })}
          confirmation={null}
          onCancel={() => props.onCancel()}
          onCommitted={() => props.onSuccess(props.product, 0)}
          onPendingChange={(pending) => props.onPendingChange?.(pending)}
          reveal={reveal}
          testIdPrefix="tree-unlock"
        />
      }
    >
      {(price) => <PaidUnlockFlow {...props} price={price()} reveal={reveal} />}
    </Show>
  )
}

function PaidUnlockFlow(
  props: UnlockFlowProps & {
    price: PaidCataloguePrice
    reveal: () => JSXElement
  },
) {
  // Live effective price (ADR 0027): the confirmation re-derives as the
  // server clock crosses a sale-window edge — no snapshot at open.
  const { amount, original } = createEffectivePriceParts(
    () => props.price.pricing,
    () => props.serverNow(),
  )
  const confirmationDescription = () => {
    const values = {
      price: numberFormatter.format(amount()),
      tree: props.product.title,
    }
    return props.price.currency === 'coin'
      ? m.unlock_tree_confirm_with_coins(values)
      : m.purchase_confirm_with_time_crystals(values)
  }

  const { adapter, submittedAmount } = captureSubmittedAmount(
    softPurchaseAdapter({
      affordable: () =>
        canAfford(props.balances(), {
          currency: props.price.currency,
          price: amount(),
        }),
      productId: () => props.product.id,
      userId: () => props.userId,
    }),
    amount,
  )

  return (
    <PurchaseFlow
      adapter={adapter}
      balancePills={props.balances}
      confirmation={{
        cta: (
          <ConfirmPriceCta
            amount={amount()}
            currency={props.price.currency}
            original={original()}
          />
        ),
        description: confirmationDescription(),
        title:
          props.product.content === 'land_skin'
            ? m.unlock_land_confirm_title()
            : m.unlock_tree_confirm_title(),
      }}
      insufficient={{
        kind: 'escalate',
        onShort: () => props.onTopUp(props.price.currency),
      }}
      onCancel={() => props.onCancel()}
      onCommitted={() => props.onSuccess(props.product, submittedAmount())}
      reveal={props.reveal}
      testIdPrefix="tree-unlock"
    />
  )
}
